/**
 * useChatAutoScroll — encapsulates every auto-scroll concern for the
 * chat surface. Single responsibility: keep the latest message visible
 * above the composer.
 *
 * Contract (load-bearing):
 *   Messages must NEVER touch the composer EXCEPT when the user has
 *   manually scrolled up. This hook holds that invariant via a
 *   forward-driven ResizeObserver pinned to the last-message slot —
 *   not via heuristic auto-tail (which drifts under bursts) or via
 *   useEffect on `messagesLength` (which doesn't fire for per-token
 *   content updates).
 *
 * Three scroll paths:
 *   1. Initial mount + chat switch — useLayoutEffect, paint-tight pin
 *      so first paint matches steady-state.
 *   2. In-flight auto-pin — ResizeObserver on the last slot div fires
 *      on EVERY layout change (per-token growth, markdown shifts,
 *      composer-height changes via --composer-h, …) and re-pins
 *      through Virtuoso's official scrollToIndex API. Suppressed when
 *      the user has scrolled up to read history.
 *   3. Imperative scrollToBottom — exposed for the ↓ button + form
 *      submit. Resets the user-scrolled-away flag so auto-pin re-engages.
 *
 * Scroll-event arbitration:
 *   atBottomStateChange is wired through the hook so we can tell our
 *   own programmatic scrolls (suppress flag set, ignore the resulting
 *   atBottom flip) apart from genuine user scrolls (no suppress flag,
 *   flip is honoured → userScrolledAwayRef = true).
 */

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { VirtuosoHandle } from 'react-virtuoso';

import type { ChatState } from './useStreamingChat';


export type ScrollBehavior = 'auto' | 'smooth';


export interface UseChatAutoScrollArgs {
    virtuosoRef: React.MutableRefObject<VirtuosoHandle | null>;
    /** `null` / `undefined` mean "no chat selected" (welcome screen,
     *  empty state). Both are treated the same; widening the type
     *  matches ChatRoom's optional prop shape. */
    chatId: string | null | undefined;
    isLoadingMessages: boolean;
    /** `undefined` is treated as `'idle'` — the empty-prop default
     *  used by ChatRoom when streaming isn't wired up (read-only
     *  views, tests). */
    chatState: ChatState | undefined;
    /** Length only. The hook re-pins to the last item on chat switch;
     *  per-token re-pins are driven by the ResizeObserver, not by
     *  React state. */
    messagesLength: number;
}


export interface UseChatAutoScrollReturn {
    /** Imperative scroll-to-bottom. Used by the floating ↓ button
     *  and any caller that wants to explicitly return to the latest
     *  message. Resets userScrolledAwayRef so the in-flight auto-pin
     *  re-engages — that's the ↓ button's whole purpose. */
    scrollToBottom: (behavior?: ScrollBehavior) => void;
    /** Ref-callback for the .messages-last-slot wrapper element.
     *  Spread it on the slot div in ChatRoom's itemContent. The hook
     *  uses it to install a ResizeObserver on the slot for the
     *  forward-driven auto-pin. Stable identity. */
    setLastSlotEl: (el: HTMLElement | null) => void;
    /** Wire into Virtuoso's `atBottomStateChange` prop. The hook uses
     *  this to track whether the user has manually scrolled away from
     *  the bottom (which suppresses auto-pin per the contract). */
    onAtBottomStateChange: (atBottom: boolean) => void;
}


export function useChatAutoScroll({
    virtuosoRef,
    chatId,
    isLoadingMessages,
    chatState,
    messagesLength,
}: UseChatAutoScrollArgs): UseChatAutoScrollReturn {
    // Refs mirror live values so the RO callback (which lives outside
    // React's render cycle) and the imperative scrollToBottom can read
    // the latest values without re-creating closures every render.
    // Initialised on first render; useLayoutEffect keeps them in sync
    // every commit (sync, before paint — matches the previous render-
    // time write semantics without violating react-hooks/refs).
    const virtuosoRefRef = useRef(virtuosoRef);
    const messagesLengthRef = useRef(messagesLength);
    const chatStateRef = useRef(chatState);
    useLayoutEffect(() => {
        virtuosoRefRef.current = virtuosoRef;
        messagesLengthRef.current = messagesLength;
        chatStateRef.current = chatState;
    });

    // Whether the user has manually scrolled away from the bottom.
    // Set by onAtBottomStateChange when atBottom flips false from a
    // user-initiated scroll. Per contract, when true we suppress the
    // in-flight auto-pin so the user can read history without being
    // yanked back.
    const userScrolledAwayRef = useRef(false);

    // Timestamp of the most-recent programmatic scroll. Smooth scrolls
    // fire multiple atBottomStateChange events as scroll progresses
    // through threshold zones, so a single-shot suppress flag would
    // miss the trailing events and let them mis-attribute as user
    // scrolls. We use a time window: any atBottomStateChange within
    // SUPPRESS_WINDOW_MS of our last programmatic scroll is treated
    // as fallout from that scroll, not a user action.
    const lastProgrammaticScrollAtRef = useRef(0);
    const SUPPRESS_WINDOW_MS = 600;

    // While a USER-initiated smooth scroll (↓ button) is mid-animation,
    // the RO must NOT fire its own instant scrollToIndex — that would
    // cut the smooth animation short and feel like a glitch. The flag
    // is set true on smooth scrollToBottom and cleared after the
    // expected animation duration. Per-token RO ticks during streaming
    // resume normally once cleared.
    const smoothScrollInProgressRef = useRef(false);
    const SMOOTH_SCROLL_DURATION_MS = 500;

    // The currently-attached observer + element. Either both null or
    // both set — invariant maintained by setLastSlotEl below.
    const lastSlotElRef = useRef<HTMLElement | null>(null);
    const roRef = useRef<ResizeObserver | null>(null);

    /** ChatRoom passes this as the slot div's `ref` prop. React calls
     *  it with the element on mount, with null on unmount, and with
     *  a new element if React decides to re-mount the slot (chat
     *  switch, role-swap from optimistic-user → assistant placeholder).
     *  Stable identity — does NOT recreate per render. */
    const setLastSlotEl = useCallback((el: HTMLElement | null) => {
        // Always tear down the prior observer first. RO can be
        // re-targeted via .observe(newEl), but disconnect+new is
        // simpler reasoning and the cost is negligible.
        if (roRef.current) {
            roRef.current.disconnect();
            roRef.current = null;
        }
        lastSlotElRef.current = el;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            const inFlight =
                chatStateRef.current === 'streaming' ||
                chatStateRef.current === 'submitting';
            if (!inFlight) return;
            if (userScrolledAwayRef.current) return;
            // Don't interrupt a user's smooth-scroll animation in
            // progress (e.g. ↓ button) — the instant scrollToIndex
            // we'd fire here would cut their animation short.
            if (smoothScrollInProgressRef.current) return;
            // Programmatic scroll — record the timestamp so the
            // resulting atBottomStateChange events (which fire as the
            // scroll passes through threshold zones) aren't
            // mis-attributed to the user.
            lastProgrammaticScrollAtRef.current = performance.now();
            virtuosoRefRef.current.current?.scrollToIndex({
                index: 'LAST',
                align: 'end',
                behavior: 'auto',
            });
        });
        ro.observe(el);
        roRef.current = ro;
    }, []);

    // Cleanup on hook unmount. The ref-callback's tear-down handles
    // re-attach during the component's lifetime; this is just for the
    // final unmount case where ref-callback fires with null in cleanup.
    useEffect(() => () => {
        if (roRef.current) {
            roRef.current.disconnect();
            roRef.current = null;
        }
    }, []);

    /** Wire into Virtuoso's atBottomStateChange. */
    const onAtBottomStateChange = useCallback((atBottom: boolean) => {
        const elapsed = performance.now() - lastProgrammaticScrollAtRef.current;
        if (elapsed < SUPPRESS_WINDOW_MS) {
            // Within the post-scroll window — the flip is fallout from
            // our own programmatic scroll, not a user action. Ignore it.
            return;
        }
        userScrolledAwayRef.current = !atBottom;
    }, []);

    /** Imperative scroll-to-bottom. Resets userScrolledAwayRef so the
     *  in-flight auto-pin re-engages — this is the ↓ button's whole
     *  purpose: explicit "take me back to bottom and follow again". */
    const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
        if (messagesLengthRef.current === 0) return;
        userScrolledAwayRef.current = false;
        lastProgrammaticScrollAtRef.current = performance.now();
        if (behavior === 'smooth') {
            // Gate the RO callback for the duration of the smooth
            // animation so per-token RO ticks don't interrupt with
            // an instant scrollToIndex mid-animation.
            smoothScrollInProgressRef.current = true;
            window.setTimeout(() => {
                smoothScrollInProgressRef.current = false;
            }, SMOOTH_SCROLL_DURATION_MS);
        }
        virtuosoRefRef.current.current?.scrollToIndex({
            index: 'LAST',
            align: 'end',
            behavior,
        });
    }, []);

    // Path 1: jump to latest on mount + chat switch — paint-tight via
    // useLayoutEffect so the scroll happens before the browser paints.
    // Combined with useComposerSize's commit-phase --composer-h write,
    // first paint matches steady-state and the user never sees a
    // "wrong position" frame.
    //
    // Deps deliberately exclude messagesLength: this effect is for
    // the chat-open / chat-switch axis only. Per-message and per-token
    // re-pins are owned by the ResizeObserver above.
    useLayoutEffect(() => {
        if (!isLoadingMessages) scrollToBottom('auto');
    }, [chatId, isLoadingMessages, scrollToBottom]);

    return { scrollToBottom, setLastSlotEl, onAtBottomStateChange };
}
